/**
 * NotificationContext.tsx
 *
 * Provides in-app notifications for CBOps via:
 *   1. Initial fetch — loads the 50 most recent notifications for the current user.
 *   2. Supabase Realtime — three event listeners on the `notifications` table,
 *      all filtered by user_id:
 *        • INSERT — new rows are prepended (with a dedupe guard against the
 *          initial fetch race) and trigger a toast pop-up.
 *        • UPDATE — cross-tab read-state sync; merging any column changes.
 *        • DELETE — removes the row from local state + drops any pending toast,
 *          so future "Delete" / "Clear all" actions sync across tabs.
 *
 * Exposed via useNotifications():
 *   notifications   — AppNotification[] sorted newest-first
 *   unreadCount     — number of notifications where read_at is null
 *   loading         — true during initial fetch
 *   markRead(id)    — sets read_at = now() for one notification
 *   markAllRead()   — sets read_at = now() for all unread notifications
 *   toasts          — NotificationToast[] pending display (auto-dismissed after 4 s)
 *   dismissToast(id)— removes a toast from the queue early
 *
 * The Realtime subscription is created once when the user is authenticated and
 * torn down when the component unmounts or the user signs out.
 *
 * DB table required:
 *   See CHANGELOG.md v3.8.0 for the CREATE TABLE / RLS / index SQL.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { supabase } from '../lib/supabase';
import { api, apiConfigured } from '../lib/apiClient';
import { subscribeRoom, RealtimeEnvelope } from '../lib/realtimeClient';
import { useAuth } from './AuthContext';
import type { AppNotification } from '../types';

interface BackendListResponse {
  items: AppNotification[];
  total: number;
  page: number;
  page_size: number;
}

// ── Toast type ────────────────────────────────────────────────────────────────

export interface NotificationToast {
  id: string;
  notification: AppNotification;
}

// ── Context shape ─────────────────────────────────────────────────────────────

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  /** Set when the inbox fetch errored — surfaced by consumers so a failed load
   *  never looks like a merely-empty inbox. */
  error: string | null;
  markRead:    (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Hard-delete a single notification row. Realtime DELETE event will sync
   *  every other open tab. */
  deleteNotification: (id: string) => Promise<void>;
  /** Hard-delete every notification that's already been read. Useful for
   *  inbox spring-cleaning without losing pending alerts. */
  clearAllRead: () => Promise<number>;
  /** Re-fetch the inbox (e.g. after an automation inserts a notification, in case
   *  realtime didn't deliver it to this tab). */
  refresh: () => Promise<void>;
  toasts: NotificationToast[];
  dismissToast: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount:   0,
  loading:       false,
  error:         null,
  markRead:      async () => {},
  markAllRead:   async () => {},
  deleteNotification: async () => {},
  clearAllRead: async () => 0,
  refresh:       async () => {},
  toasts:        [],
  dismissToast:  () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

const FETCH_LIMIT = 50;
const TOAST_TTL_MS = 4000;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<NotificationToast[]>([]);
  /** Guard: skip toasting on initial batch load, only toast on realtime inserts. */
  const initialLoadDone = useRef(false);

  // ── Initial fetch ───────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async (opts?: { silent?: boolean }) => {
    if (!user) return;
    if (!opts?.silent) setLoading(true);
    if (!opts?.silent) setError(null);

    // Phase-2 path. Backend GET /notifications honours the same
    // workspace + user-id filtering we'd apply via Supabase, so the
    // result set is identical. Mirrors the v6.12 NotificationsPage
    // migration shape.
    if (apiConfigured()) {
      const res = await api.get<BackendListResponse>(
        `/notifications?page=1&page_size=${FETCH_LIMIT}`,
      );
      if (res.ok) {
        setNotifications(res.data.items);
        if (!opts?.silent) setLoading(false);
        initialLoadDone.current = true;
        return;
      }
      console.warn(
        '[NotificationContext] backend list failed (status=%d): %s — falling back to Supabase',
        res.status,
        res.error,
      );
    }

    // Phase-1 fallback path
    const { data, error } = await supabase
      .from('notifications')
      .select('*, actor:profiles!notifications_actor_id_fkey(full_name, avatar_url)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(FETCH_LIMIT);

    // Never fail silently: surface + log a real fetch error instead of leaving
    // the inbox looking merely empty (the Announcements-class bug).
    if (error) {
      console.error('[NotificationContext] fetchNotifications error:', error);
      setError(error.message || 'Could not load notifications.');
    } else {
      setNotifications((data as unknown as AppNotification[]) ?? []);
    }
    if (!opts?.silent) setLoading(false);
    initialLoadDone.current = true;
  }, [user]);

  useEffect(() => {
    initialLoadDone.current = false;
    setNotifications([]);
    fetchNotifications();
  }, [fetchNotifications]);

  // ── Live subscription ───────────────────────────────────────────────────────
  //
  // Two transports, one handler:
  //   Phase-2: our own SSE stream (`subscribeRoom` on `user:<id>`) — the
  //   envelope is shaped exactly like a postgres_changes payload, so the
  //   handler below is shared. `onOpen` re-fetches silently to close any gap
  //   a reconnect left (Redis pub/sub has no replay).
  //   Phase-1: the Supabase Realtime channel, used while the workspace is not
  //   opted in. This is what keeps the bell working until the cutover.

  const handleLiveEvent = useCallback((event: RealtimeEnvelope) => {
    if (event.t !== 'notifications') return;

    if (event.op === 'INSERT' && event.new) {
      const newNotif = event.new as unknown as AppNotification;
      setNotifications(prev => {
        // Live delivery can race the initial fetch; skip if we already have it.
        if (prev.some(n => n.id === newNotif.id)) return prev;
        return [newNotif, ...prev];
      });

      // Show a toast for every live notification (not initial load)
      if (initialLoadDone.current) {
        const toastId = newNotif.id;
        setToasts(prev => [...prev, { id: toastId, notification: newNotif }]);
        // Auto-dismiss after TTL
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== toastId));
        }, TOAST_TTL_MS);
      }
      return;
    }

    if (event.op === 'UPDATE' && event.new) {
      // Cross-tab read-state sync: marking read in one tab updates all others.
      const updated = event.new as unknown as AppNotification;
      setNotifications(prev =>
        prev.map(n => (n.id === updated.id ? { ...n, ...updated } : n))
      );
      return;
    }

    if (event.op === 'DELETE') {
      // Hard deletes sync too.
      const removedId = event.old?.id;
      if (!removedId) return;
      setNotifications(prev => prev.filter(n => n.id !== removedId));
      setToasts(prev => prev.filter(t => t.id !== removedId));
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    if (apiConfigured()) {
      // Skip the first connect — the mount fetch already covered it. A
      // re-connect (EventSource retry / token rebuild) means events may have
      // been missed, so resync silently.
      let firstOpen = true;
      const sub = subscribeRoom(`user:${user.id}`, {
        onEvent: handleLiveEvent,
        onOpen: () => {
          if (firstOpen) {
            firstOpen = false;
            return;
          }
          void fetchNotifications({ silent: true });
        },
        onError: () => { /* EventSource keeps retrying on its own */ },
      });
      return () => sub.close();
    }

    // Phase-1 fallback — Supabase Realtime.
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event:  'INSERT',
          schema: 'public',
          table:  'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: { new?: Record<string, unknown> }) => {
          handleLiveEvent({
            t: 'notifications',
            op: 'INSERT',
            new: payload.new as Record<string, unknown>,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: { new?: Record<string, unknown> }) => {
          handleLiveEvent({
            t: 'notifications',
            op: 'UPDATE',
            new: payload.new as Record<string, unknown>,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event:  'DELETE',
          schema: 'public',
          table:  'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: { old?: { id?: string } | null }) => {
          handleLiveEvent({
            t: 'notifications',
            op: 'DELETE',
            old: (payload.old as { id?: string }) ?? null,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, handleLiveEvent, fetchNotifications]);

  // ── Mark read ───────────────────────────────────────────────────────────────

  const markRead = useCallback(async (id: string) => {
    const now = new Date().toISOString();
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read_at: now } : n)
    );
    // Phase-2 path
    if (apiConfigured()) {
      const res = await api.post(`/notifications/${id}/read`);
      if (res.ok) return;
      console.warn(
        '[NotificationContext] backend mark-read failed (status=%d): %s — falling back',
        res.status,
        res.error,
      );
    }
    // Phase-1 fallback
    await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('id', id);
  }, []);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? now })));
    if (apiConfigured()) {
      const res = await api.post('/notifications/read-all');
      if (res.ok) return;
      console.warn(
        '[NotificationContext] backend mark-all-read failed (status=%d): %s — falling back',
        res.status,
        res.error,
      );
    }
    await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('user_id', user.id)
      .is('read_at', null);
  }, [user]);

  // ── Delete actions ──────────────────────────────────────────────────────────

  const deleteNotification = useCallback(async (id: string) => {
    // Optimistic local removal — realtime DELETE will reconcile other tabs.
    setNotifications(prev => prev.filter(n => n.id !== id));
    setToasts(prev => prev.filter(t => t.id !== id));
    if (apiConfigured()) {
      const res = await api.delete(`/notifications/${id}`);
      if (res.ok) return;
      console.warn(
        '[NotificationContext] backend delete failed (status=%d): %s — falling back',
        res.status,
        res.error,
      );
    }
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id);
    if (error) {
      console.error('[NotificationContext] deleteNotification failed', error);
    }
  }, []);

  const clearAllRead = useCallback(async (): Promise<number> => {
    if (!user) return 0;
    const readIds = notifications.filter(n => n.read_at).map(n => n.id);
    if (!readIds.length) return 0;
    setNotifications(prev => prev.filter(n => !n.read_at));
    if (apiConfigured()) {
      const res = await api.delete('/notifications/read');
      if (res.ok) return readIds.length;
      console.warn(
        '[NotificationContext] backend clear-all-read failed (status=%d): %s — falling back',
        res.status,
        res.error,
      );
    }
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id)
      .not('read_at', 'is', null);
    if (error) {
      console.error('[NotificationContext] clearAllRead failed', error);
    }
    return readIds.length;
  }, [user, notifications]);

  // ── Dismiss toast ───────────────────────────────────────────────────────────

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      error,
      markRead,
      markAllRead,
      deleteNotification,
      clearAllRead,
      refresh: fetchNotifications,
      toasts,
      dismissToast,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useNotifications() {
  return useContext(NotificationContext);
}
